import React from 'react';
import {
    BUDGET_YEAR_FIELD,
    PERCENT_REALIZED_FIELD,
    REALIZED_FIELD,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
    BUDGET_LINE_INSCRIPTION_AMOUNT,
    PROJECTION_DETAIL_AMOUNT,
    PROJECTION_DETAIL_COMMENT,
    PROJECTION_ID,
    PROJECTION_ATTERRISSAGE_STATUS,
    SPLIT_BY,
    PROJECTION_PLURIANNUAL_STATUS,
    SERVICE_PROPOSAL_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    PHASE_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import { realizedRateValueGetter } from 'ui/AgGridReact/getters';

import { translateBlockValue } from 'i18n';
import { realizedRateAggFunc } from 'ui/AgGridReact/aggFuncs';
import { ColDef, IAggFunc } from 'ag-grid-community';
import { Theme } from '@mui/material';
import {
    ResourceSoftwareIdField,
    BudgetProposalT,
} from 'routes/BudgetModule/config/types';
import { BudgetExpenseOrIncome, BudgetPhases } from 'business/constants';
import { getResourceSoftwareIdField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { startAdornment } from 'ui/AgGridReact/CurrencyInputAdornment';
import { parseNumericInput } from 'helpers/UIHelpers';
import NumberFormatCustom from 'components/NumberFormat/NumberFormatCustom';
import {
    ProjectionsDimensionsMapping,
    PluriannualProjectionDetails,
    ProjectionVentilation,
    AtterrissageProjectionDetails,
    AbstractProjectionDetailsT,
} from './types';
import {
    CellRendererParamsProjectionStatusAndSidePanelT,
    CellRendererStatusAndSidePanelValueT,
} from '../Components/ProjectionStatusSidePanelCellRenderer';
import { AtterrissageProjectionDetailsCellRendererCustomParams } from '../Components/AtterrissageProjectionDetailCellRenderer';
import TotalProjectionsHeader, {
    AgGridHeaderComponentProps,
} from '../Components/TotalProjectionsHeader';
import { PluriannualProjectionDetailsCellRendererCustomParams } from '../Components/PluriannualProjectionDetailCellRenderer';
import {
    getBudgetExcelCurrencyCellClass,
    getBudgetExcelStringCellClass,
    getBudgetExcelPercentageCellClass,
} from 'routes/BudgetModule/config/excelExportHelpers';
import { currencyFormatter, percentageFormatter } from 'helpers/formatHelper';

const NUMERIC_COLUMN: ColDef = {
    enableRowGroup: false,
    aggFunc: 'sum',
    valueFormatter: (params) => currencyFormatter(params.value),
    useValueFormatterForExport: false,
    enableValue: true,
    enablePivot: false,
    suppressHeaderMenuButton: true,
    type: 'numericColumn',
    cellClass: getBudgetExcelCurrencyCellClass,
};

export type UpdateProjectionDetailsParams = {
    budgetProposal: BudgetProposalT;
    expenseOrIncome: BudgetExpenseOrIncome;
    resourceSoftwareIdField: string;
    isDaf: boolean;
};

type GetAtterrissageColDefsParams = {
    cellRendererCustomParams: AtterrissageProjectionDetailsCellRendererCustomParams;
    exercice: number;
    isDaf: boolean;
    statusCellRendererCustomParams: CellRendererParamsProjectionStatusAndSidePanelT;
    t: TranslationT;
    theme: Theme;
    updateProjectionParams: UpdateProjectionDetailsParams;
    dimensionsMapping: ProjectionsDimensionsMapping;
    budgetProposal: BudgetProposalT;
    softwares: Set<string>;
};

/**
 * Given a list projectionsBy (projectionBy or pluriannualProjectionBy),
 * build ventilations grid columns dynamically.
 */
const buildProjectionsVentilationsColDefs = ({
    resourceSoftwareIdField,
    projectionBy = [],
    dimensionsMapping,
    isDaf,
}: {
    resourceSoftwareIdField: ResourceSoftwareIdField;
    projectionBy: ProjectionVentilation.SoftwareIds[];
    dimensionsMapping: ProjectionsDimensionsMapping;
    isDaf: boolean;
}) =>
    [resourceSoftwareIdField, ...projectionBy].map((el, index) => {
        const getColReadableValue = (value: string) =>
            dimensionsMapping[el].mapping[value] || '';
        const coldef: ColDef = {
            field: el,
            rowGroupIndex: index,
            rowGroup: true,
            pivot: false,
            hide: !isDaf && el === resourceSoftwareIdField,
        };
        return {
            ...coldef,
            filter: 'agTextColumnFilter',
            suppressColumnsToolPanel: true,
            valueFormatter: ({ value }: { value: string }) =>
                getColReadableValue(value),
            comparator: (a: string, b: string) => {
                return getColReadableValue(a).localeCompare(
                    getColReadableValue(b),
                );
            },
        };
    });

/**
 * Build the list of colDefs that should be displayed on a specific phase
 * @param phase the phase of the budget proposal
 * @returns a list of colDefs
 */
const getPhaseAmountColumns = (
    t: TranslationT,
    theme: Theme,
    budgetProposal: BudgetProposalT,
): ColDef[] => [
    {
        headerName: t('projection.serviceProposalAmount'),
        field: PHASE_AMOUNT,
        ...NUMERIC_COLUMN,
        valueGetter: ({ data }) => data?.[SERVICE_PROPOSAL_AMOUNT],
        hide: budgetProposal.phase !== BudgetPhases.PROPOSITION_SERVICES,
        cellStyle: {
            color: theme.palette.primary.main,
        },
    },
    {
        headerName: t('projection.primitiveBudgetAmount'),
        field: PHASE_AMOUNT,
        ...NUMERIC_COLUMN,
        valueGetter: ({ data }) => data?.[PRIMITIVE_BUDGET_AMOUNT],
        hide: budgetProposal.phase !== BudgetPhases.VOTE,
        cellStyle: {
            color: theme.palette.primary.main,
        },
    },
    {
        headerName: t('projection.simulatedArbitratedAmount'),
        field: PHASE_AMOUNT,
        ...NUMERIC_COLUMN,
        valueGetter: ({ data }) => data?.[SIMULATED_ARBITRATED_AMOUNT],
        hide: budgetProposal.phase !== BudgetPhases.ARBITRAGE,
        cellStyle: {
            color: theme.palette.primary.main,
        },
    },
    {
        headerName: t('projection.supplementaryBudgetAmount'),
        field: PHASE_AMOUNT,
        ...NUMERIC_COLUMN,
        valueGetter: ({ data }) => data?.[SUPPLEMENTARY_BUDGET_AMOUNT],
        hide: budgetProposal.phase !== BudgetPhases.SUPPLEMENTARY_BUDGET,
        cellStyle: {
            color: theme.palette.primary.main,
        },
    },
];

const isProjectionValidForYear = (
    data: AbstractProjectionDetailsT,
    exercice: number,
) => data && data[BUDGET_YEAR_FIELD] === exercice;

export const getAtterrissageColDefs = ({
    cellRendererCustomParams,
    exercice,
    isDaf,
    statusCellRendererCustomParams,
    t,
    theme,
    updateProjectionParams,
    dimensionsMapping,
    budgetProposal,
    softwares,
}: GetAtterrissageColDefsParams): ColDef[] => {
    const primaryColor = theme.palette.primary.main;
    const resourceSoftwareIdField: ResourceSoftwareIdField =
        getResourceSoftwareIdField(budgetProposal.by);

    // using a React component in a header cell is too painful for now, so we use the underlying svg from MUI InfoIcon
    const infoIcon = `
    <svg style="margin-left:4px;height:1em;width:1em;fill:${primaryColor}" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
        </path>
    </svg>`;
    const colDefs: ColDef[] = [
        {
            field: BUDGET_YEAR_FIELD,
            rowGroup: false,
            pivot: true,
            filter: true,
            suppressFiltersToolPanel: false,
            suppressColumnsToolPanel: true,
        },
        ...buildProjectionsVentilationsColDefs({
            resourceSoftwareIdField,
            projectionBy: budgetProposal?.projectionBy,
            dimensionsMapping,
            isDaf,
        }),
        {
            field: VOTED_FIELD, // Primitif
            ...NUMERIC_COLUMN,
        },
        {
            field: VOTED_TOTAL_FIELD, // Budgété total
            ...NUMERIC_COLUMN,
        },
        {
            field: REALIZED_FIELD, // Réalisé
            ...NUMERIC_COLUMN,
        },
        {
            field: PERCENT_REALIZED_FIELD,

            valueFormatter: (params) => percentageFormatter(params.value),
            valueGetter: realizedRateValueGetter,
            cellClass: getBudgetExcelPercentageCellClass,
            useValueFormatterForExport: false,
            aggFunc: 'realizedRateAggFunc',
            suppressHeaderMenuButton: true,
            enableValue: true,
            type: 'numericColumn',
        },
        ...getPhaseAmountColumns(
            t,
            theme,
            updateProjectionParams.budgetProposal,
        ),
        {
            field: PROJECTION_DETAIL_AMOUNT,
            headerName: t('projection.columnHeader', { exercice }),
            aggFunc: 'sum',
            cellRenderer: 'atterrissageProjectionDetailCellRenderer',
            // We only render a numeric value for column of the current exercice, otherwise we send null wich cause the cell to be disabled
            valueGetter: ({
                data,
            }: {
                data: AtterrissageProjectionDetails.WithMetadata;
            }) =>
                isProjectionValidForYear(data, exercice)
                    ? data[PROJECTION_DETAIL_AMOUNT] || 0
                    : null,
            cellRendererParams: {
                updateProjectionParams,
                customParams: cellRendererCustomParams,
                field: PROJECTION_DETAIL_AMOUNT,
                defaultValue: 0,
                valueTransformer: parseNumericInput,
                textFieldProps: {
                    placeholder: t('projection.amountPlaceholder'),
                    InputProps: {
                        startAdornment,
                        inputComponent: NumberFormatCustom,
                    },
                },
            },
            width: 135,
            pinned: 'right',
            cellStyle: {
                background: theme.palette.lightGrey!,
            },
            cellClass: getBudgetExcelCurrencyCellClass,
            type: 'numericColumn',
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
            suppressKeyboardEvent: () => true, // so we can use keys to navigate within and between the inputs
        },
        {
            field: 'projection_obsolete',
            aggFunc: 'sum',
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
            valueGetter: ({
                data,
            }: {
                data: AtterrissageProjectionDetails.WithMetadata;
            }) => (isProjectionValidForYear(data, exercice) ? 0 : null),
        },
        {
            field: 'projection_full_amount_difference',
            headerName: 'Écart',
            aggFunc: 'sum',
            cellStyle: (params: { value: number }) => {
                const upColor =
                    updateProjectionParams.expenseOrIncome ===
                    BudgetExpenseOrIncome.EXPENSE
                        ? theme.palette.red!
                        : theme.palette.green!;
                const downColor =
                    updateProjectionParams.expenseOrIncome ===
                    BudgetExpenseOrIncome.EXPENSE
                        ? theme.palette.green!
                        : theme.palette.red!;
                let color = theme.palette.green!;
                if (params.value !== 0) {
                    color = params.value > 0 ? upColor : downColor;
                }
                return {
                    color,
                    background: theme.palette.lightGrey!,
                    '--ag-value-change-delta-up-color': upColor,
                    '--ag-value-change-delta-down-color': downColor,
                };
            },
            valueGetter: ({
                data,
            }: {
                data: AtterrissageProjectionDetails.WithMetadata;
            }) => {
                if (!data) return 0;
                const difference =
                    (data[PROJECTION_DETAIL_AMOUNT] ?? 0) -
                    data[VOTED_TOTAL_FIELD];
                const isInvalid = Number.isNaN(difference);
                return isInvalid ? 0 : difference;
            },
            valueFormatter: (params) =>
                currencyFormatter(params.value, { showSign: true }),
            cellClass: getBudgetExcelCurrencyCellClass,
            useValueFormatterForExport: false,
            cellRenderer: 'agAnimateShowChangeCellRenderer',
            cellRendererParams: {
                // This allows us to override the animation formatting and to not format it.
                formatValue: (value: number) => value,
            },
            width: 120,
            pinned: 'right',
            suppressMovable: true,
            headerComponentParams: {
                template: `
                <div class="ag-cell-label-container" role="presentation">
                    <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>
                    <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button" aria-hidden="true"></span>
                    <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span data-ref="eText" class="ag-header-cell-text"></span>${infoIcon}
                        <span data-ref="eFilter" class="ag-header-icon ag-header-label-icon ag-filter-icon" aria-hidden="true"></span>
                        <span data-ref="eSortOrder" class="ag-header-icon ag-header-label-icon ag-sort-order" aria-hidden="true"></span>
                        <span data-ref="eSortAsc" class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon" aria-hidden="true"></span>
                        <span data-ref="eSortDesc" class="ag-header-icon ag-header-label-icon ag-sort-descending-icon" aria-hidden="true"></span>
                        <span data-ref="eSortNone" class="ag-header-icon ag-header-label-icon ag-sort-none-icon" aria-hidden="true"></span>
                    </div>
                </div>`,
            },
            headerTooltip: t('projection.differenceTooltip'),
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
        },
        {
            field: PROJECTION_DETAIL_COMMENT,
            minWidth: 250,
            headerName: t('projection.justification'),
            aggFunc: (params: { values: string[] }) => params.values[0],
            cellRenderer: 'atterrissageProjectionDetailCellRenderer',
            // We only render a string value for column of the current exercice, otherwise we send null wich cause the cell to be disabled
            valueGetter: ({
                data,
            }: {
                data: AtterrissageProjectionDetails.WithMetadata;
            }) =>
                isProjectionValidForYear(data, exercice)
                    ? data[PROJECTION_DETAIL_COMMENT] || ''
                    : null,
            cellRendererParams: {
                updateProjectionParams,
                customParams: cellRendererCustomParams,
                field: PROJECTION_DETAIL_COMMENT,
            },
            pinned: 'right',
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
            cellStyle: { background: theme.palette.lightGrey! },
            suppressMovable: true,
            hide: isDaf,
            suppressKeyboardEvent: () => true, // so we can use keys to navigate within and between the inputs
        },
        // this column displays a status dropdown when row is grouped, or a sidepanel button otherwise
        {
            field: 'StatusAndSidePanel',
            headerName: t('common.status'),
            cellRenderer: 'projectionStatusSidepanelCellRenderer',
            aggFunc: (params: { values: string[] }) =>
                params.values.filter((value) => value)[0],
            pinned: 'right',
            cellStyle: {
                background: theme.palette.lightGrey!,
                justifyContent: 'center',
            },
            cellRendererParams: {
                updateProjectionParams,
                customParams: statusCellRendererCustomParams,
                statusField: 'atterrissageStatus',
            },
            cellClass: getBudgetExcelStringCellClass,
            width: 110,
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
            valueGetter: ({
                data,
            }: {
                data?: AtterrissageProjectionDetails.WithMetadata;
            }): CellRendererStatusAndSidePanelValueT => {
                const resourceSoftwareId = data?.[resourceSoftwareIdField];
                return {
                    id: data?.[PROJECTION_ID],
                    atterrissageStatus: data?.[PROJECTION_ATTERRISSAGE_STATUS],
                    resourceSoftwareId,
                    projectionDetailAmount: data?.[PROJECTION_DETAIL_AMOUNT],
                    projectionDetailComment: data?.[PROJECTION_DETAIL_COMMENT],
                };
            },
            // used for excel export
            valueFormatter: ({ value }) =>
                translateBlockValue(value?.atterrissageStatus ?? ''),
            suppressMovable: true,
            hide: !isDaf,
        },
    ];
    return colDefs
        .filter((col) => !col.hide)
        .map((col) => ({
            ...col,
            field: col.field,
            sortable: true,
            headerName:
                col.headerName ||
                translateBlockValue(col.field ?? '', true, softwares),
        }));
};

type GetPluriannualColDefsParams = {
    exercice: number;
    pluriannualProjectionBy: ProjectionVentilation.SoftwareIds[];
    isDaf: boolean;
    resourceSoftwareIdField: ResourceSoftwareIdField;
    dimensionsMapping: ProjectionsDimensionsMapping;
    t: TranslationT;
    theme: Theme;
    updateProjectionParams: UpdateProjectionDetailsParams;
    cellRendererCustomParams: PluriannualProjectionDetailsCellRendererCustomParams;
    projectionsPanedExpanded: boolean;
    toggleProjectionsPanel: () => void;
    softwares: Set<string>;
};

export const getPluriannualColDefs = ({
    exercice,
    pluriannualProjectionBy,
    isDaf,
    resourceSoftwareIdField,
    dimensionsMapping,
    updateProjectionParams,
    cellRendererCustomParams,
    t,
    theme,
    projectionsPanedExpanded,
    toggleProjectionsPanel,
    softwares,
}: GetPluriannualColDefsParams): ColDef[] => {
    const colDefs: ColDef[] = [
        {
            field: BUDGET_YEAR_FIELD,
            rowGroup: false,
            pivot: true,
            filter: true,
            suppressFiltersToolPanel: false,
            suppressColumnsToolPanel: true,
        },
        ...buildProjectionsVentilationsColDefs({
            resourceSoftwareIdField,
            projectionBy: pluriannualProjectionBy,
            dimensionsMapping,
            isDaf,
        }),
        {
            field: VOTED_FIELD, // Primitif
            ...NUMERIC_COLUMN,
        },
        {
            field: VOTED_TOTAL_FIELD, // Budgété total
            ...NUMERIC_COLUMN,
        },
        {
            field: REALIZED_FIELD, // Réalisé
            ...NUMERIC_COLUMN,
        },
        {
            field: PERCENT_REALIZED_FIELD,
            valueFormatter: (params) => percentageFormatter(params.value),
            valueGetter: realizedRateValueGetter,
            aggFunc: 'realizedRateAggFunc',
            cellClass: getBudgetExcelPercentageCellClass,
            useValueFormatterForExport: false,
            suppressHeaderMenuButton: true,
            enableValue: true,
        },
        ...getPhaseAmountColumns(
            t,
            theme,
            updateProjectionParams.budgetProposal,
        ),
        {
            field: 'projectionsTotal',
            headerName: t('projection.totalColumn'),
            headerComponent: (otherProps: AgGridHeaderComponentProps) => (
                <TotalProjectionsHeader
                    expanded={projectionsPanedExpanded}
                    toggleExpanded={toggleProjectionsPanel}
                    {...otherProps}
                />
            ),
            ...NUMERIC_COLUMN,
            width: 125,
            suppressColumnsToolPanel: true,
            pinned: 'right' as const,
            cellStyle: { background: theme.palette.lightGrey! },
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            suppressKeyboardEvent: () => true, // so we can use keys to navigate within and between the inputs
            aggFunc: 'sum',
            // We only render a string value for column of the current exercice, otherwise we send null wich cause the cell to be disabled
            valueGetter: ({
                data,
            }: {
                data: PluriannualProjectionDetails.WithMetadata;
            }) => {
                if (!data || data[BUDGET_YEAR_FIELD] !== exercice) return null;
                return [1, 2, 3, 4].reduce((agg, current) => {
                    const field = `${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${
                        exercice + current
                    }` as const;
                    const currentProjectionAmount = data[field] ?? 0;
                    return agg + currentProjectionAmount;
                }, 0);
            },
        },
        ...[1, 2, 3, 4].map((offset) => {
            const field = `${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${
                exercice + offset
            }` as const;
            return {
                field,
                headerName: (exercice + offset).toString(),
                aggFunc: 'sum',
                cellRenderer: 'pluriannualProjectionDetailCellRenderer',
                suppressColumnsToolPanel: true,
                valueGetter: ({
                    data,
                }: {
                    data: PluriannualProjectionDetails.WithMetadata;
                }) =>
                    isProjectionValidForYear(data, exercice)
                        ? (data[field] ?? 0)
                        : null,
                cellRendererParams: {
                    year: exercice + offset,
                    updateProjectionParams: {
                        ...updateProjectionParams,
                        projectionDetailsYear: exercice + offset,
                    },
                    customParams: cellRendererCustomParams,
                    field,
                    valueTransformer: parseNumericInput,
                    defaultValue: 0,
                    textFieldProps: {
                        placeholder: t('projection.amountPlaceholder'),
                        InputProps: {
                            startAdornment,
                            inputComponent: NumberFormatCustom,
                        },
                    },
                },
                width: 90,
                pinned: 'right' as const,
                cellStyle: { background: theme.palette.lightGrey! },
                cellClass: getBudgetExcelCurrencyCellClass,
                suppressMovable: true,
                suppressHeaderMenuButton: true,
                suppressKeyboardEvent: () => true, // so we can use keys to navigate within and between the inputs
                hide: !projectionsPanedExpanded,
            };
        }),
        {
            field: 'projection_obsolete',
            aggFunc: 'sum',
            suppressMovable: true,
            suppressHeaderMenuButton: true,
            suppressColumnsToolPanel: true,
            valueGetter: ({
                data,
            }: {
                data: PluriannualProjectionDetails.WithMetadata;
            }) => (isProjectionValidForYear(data, exercice) ? 0 : null),
        },
        // this column displays a status dropdown when row is grouped, or a sidepanel button otherwise
        {
            field: 'StatusAndSidePanel',
            headerName: t('common.status'),
            cellRenderer: 'projectionStatusSidepanelCellRenderer',
            aggFunc: (params: { values: string[] }) =>
                params.values.filter((value) => value)[0],
            pinned: 'right',
            suppressColumnsToolPanel: true,
            cellStyle: {
                background: theme.palette.lightGrey!,
                justifyContent: 'center',
            },
            cellRendererParams: {
                updateProjectionParams,
                statusField: 'pluriannualProjectionStatus',
                customParams: {
                    budgetProposalId: updateProjectionParams.budgetProposal.id,
                    resourceSoftwareIdField,
                    dimensionsMapping: {},
                    canEdit: true,
                },
            },
            width: 110,
            suppressHeaderMenuButton: true,
            cellClass: getBudgetExcelStringCellClass,
            valueGetter: ({
                data,
            }: {
                data?: PluriannualProjectionDetails.WithMetadata;
            }): CellRendererStatusAndSidePanelValueT => ({
                id: data?.[PROJECTION_ID],
                pluriannualStatus: data?.[PROJECTION_PLURIANNUAL_STATUS],
                resourceSoftwareId: data?.[resourceSoftwareIdField],
            }),
            valueFormatter: ({ value }) =>
                translateBlockValue(value?.pluriannualStatus ?? ''),
            suppressMovable: true,
            hide: !isDaf || !projectionsPanedExpanded,
        },
    ];

    return colDefs
        .filter((col) => !col.hide)
        .map((col) => ({
            ...col,
            sortable: true,
            headerName:
                col.headerName ||
                translateBlockValue(col.field ?? '', true, softwares),
        }));
};

export const aggFuncs: {
    [key: string]: IAggFunc;
} = { realizedRateAggFunc };

/** Customize configuration for some fields according the pivoted year under which the field is.
 *
 * For instance it allows us to display columns depending upon the pivoted year (ex: under exercice - 3 we only display the réalisé).
 */
export const getColDefsForColumnsPivotedByYear = (
    exercice: number,
): Record<number, Record<string, Pick<ColDef, 'hide'>>> => ({
    [exercice - 3]: {
        [VOTED_FIELD]: {
            hide: true,
        },
        [VOTED_TOTAL_FIELD]: {
            hide: true,
        },
        [PERCENT_REALIZED_FIELD]: {
            hide: true,
        },
        [BUDGET_LINE_INSCRIPTION_AMOUNT]: {
            hide: true,
        },
        [PROJECTION_DETAIL_AMOUNT]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 1}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 2}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 3}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 4}`]: {
            hide: true,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        amount: {
            hide: true,
        },
        projectionsTotal: {
            hide: true,
        },
        projection_obsolete: {
            hide: true,
        },
        projection_full_amount_difference: {
            hide: true,
        },
        [PROJECTION_DETAIL_COMMENT]: {
            hide: true,
        },
        StatusAndSidePanel: {
            hide: true,
        },
    },
    [exercice - 2]: {
        [VOTED_FIELD]: {
            hide: true,
        },
        [VOTED_TOTAL_FIELD]: {
            hide: true,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        amount: {
            hide: true,
        },
        [PERCENT_REALIZED_FIELD]: {
            hide: true,
        },
        [BUDGET_LINE_INSCRIPTION_AMOUNT]: {
            hide: true,
        },
        [PROJECTION_DETAIL_AMOUNT]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 1}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 2}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 3}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 4}`]: {
            hide: true,
        },
        projectionsTotal: {
            hide: true,
        },
        projection_obsolete: {
            hide: true,
        },
        projection_full_amount_difference: {
            hide: true,
        },
        [PROJECTION_DETAIL_COMMENT]: {
            hide: true,
        },
        StatusAndSidePanel: {
            hide: true,
        },
    },
    [exercice - 1]: {
        [PROJECTION_DETAIL_AMOUNT]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 1}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 2}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 3}`]: {
            hide: true,
        },
        [`${PROJECTION_DETAIL_AMOUNT}${SPLIT_BY}${exercice + 4}`]: {
            hide: true,
        },
        [SERVICE_PROPOSAL_AMOUNT]: {
            hide: true,
        },
        [PRIMITIVE_BUDGET_AMOUNT]: {
            hide: true,
        },
        [SIMULATED_ARBITRATED_AMOUNT]: {
            hide: true,
        },
        [SUPPLEMENTARY_BUDGET_AMOUNT]: {
            hide: true,
        },
        amount: {
            hide: true,
        },
        projectionsTotal: {
            hide: true,
        },
        projection_obsolete: {
            hide: true,
        },
        projection_full_amount_difference: {
            hide: true,
        },
        [PROJECTION_DETAIL_COMMENT]: {
            hide: true,
        },
        StatusAndSidePanel: {
            hide: true,
        },
    },
    [exercice]: {
        [BUDGET_LINE_INSCRIPTION_AMOUNT]: {
            hide: true,
        },
        projection_obsolete: {
            hide: true,
        },
    },
});
